import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWS } from '../../contexts/WSContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

function DeliveryMap({ customerLoc, deliveryLoc }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const L = window.L;
    if (!L) return;

    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    });

    const map = L.map(mapRef.current, { zoomControl: false }).setView(
      [customerLoc.lat, customerLoc.lng], 14,
    );
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);

    const custIcon = L.divIcon({ html: '<div style="font-size:28px">🏠</div>', className: '', iconAnchor: [14, 28] });
    const delIcon = L.divIcon({ html: '<div style="font-size:28px">🛵</div>', className: '', iconAnchor: [14, 28] });

    markersRef.current.customer = L.marker([customerLoc.lat, customerLoc.lng], { icon: custIcon })
      .bindPopup('Cliente').addTo(map);

    if (deliveryLoc) {
      markersRef.current.delivery = L.marker([deliveryLoc.lat, deliveryLoc.lng], { icon: delIcon })
        .bindPopup('Você').addTo(map);
    }
    mapObj.current = map;
  }, []);

  useEffect(() => {
    if (!mapObj.current || !deliveryLoc) return;
    const L = window.L;
    const delIcon = L.divIcon({ html: '<div style="font-size:28px">🛵</div>', className: '', iconAnchor: [14, 28] });
    if (markersRef.current.delivery) {
      markersRef.current.delivery.setLatLng([deliveryLoc.lat, deliveryLoc.lng]);
    } else {
      markersRef.current.delivery = L.marker([deliveryLoc.lat, deliveryLoc.lng], { icon: delIcon }).addTo(mapObj.current);
    }
    if (markersRef.current.line) markersRef.current.line.remove();
    markersRef.current.line = L.polyline(
      [[deliveryLoc.lat, deliveryLoc.lng], [customerLoc.lat, customerLoc.lng]],
      { color: '#FF6B00', weight: 4, dashArray: '8,6' },
    ).addTo(mapObj.current);
    mapObj.current.fitBounds(
      [[customerLoc.lat, customerLoc.lng], [deliveryLoc.lat, deliveryLoc.lng]],
      { padding: [40, 40] },
    );
  }, [deliveryLoc]);

  return <div ref={mapRef} className="w-full h-full" />;
}

export default function DeliveryActive() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const { send, connect } = useWS();
  const [order, setOrder] = useState(null);
  const [myLoc, setMyLoc] = useState(null);
  const [leafletReady, setLeafletReady] = useState(false);
  const [step, setStep] = useState('accepted'); // accepted | in_transit

  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    s.onload = () => setLeafletReady(true);
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) connect(token);
  }, []);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then(r => {
      setOrder(r.data);
      setStep(r.data.status);
      if (r.data.deliveryLocation) setMyLoc(r.data.deliveryLocation);
    });
  }, [orderId]);

  // Simulate GPS movement towards customer
  useEffect(() => {
    if (!order) return;
    const customerLoc = order.location || { lat: -23.5505, lng: -46.6333 };
    let currentLoc = myLoc || { lat: customerLoc.lat - 0.02, lng: customerLoc.lng - 0.02 };

    const t = setInterval(() => {
      const dlat = (customerLoc.lat - currentLoc.lat) * 0.15;
      const dlng = (customerLoc.lng - currentLoc.lng) * 0.15;
      currentLoc = { lat: currentLoc.lat + dlat, lng: currentLoc.lng + dlng };
      setMyLoc({ ...currentLoc });
      send({ type: 'location_update', location: currentLoc });
      api.put(`/orders/${orderId}/location`, { location: currentLoc }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [order]);

  const startTransit = async () => {
    await api.put(`/orders/${orderId}/status`, { status: 'in_transit' });
    setStep('in_transit');
    toast.success('Iniciando entrega!');
  };

  const deliver = async () => {
    try {
      await api.put(`/orders/${orderId}/status`, { status: 'delivered' });
      toast.success('Entrega confirmada! Valor creditado 🎉');
      nav('/entregador/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao confirmar');
    }
  };

  if (!order) return <div className="screen bg-gray-900 flex items-center justify-center"><div className="text-4xl animate-spin">🔥</div></div>;

  const customerLoc = order.location || { lat: -23.5505, lng: -46.6333 };

  return (
    <div className="screen flex flex-col bg-gray-900">
      {/* Map */}
      <div className="flex-1 relative" style={{ minHeight: 0 }}>
        {leafletReady ? (
          <DeliveryMap customerLoc={customerLoc} deliveryLoc={myLoc} />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <span className="text-3xl animate-spin">🗺</span>
          </div>
        )}
        {/* Top overlay */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-gray-900/90 to-transparent px-4 pt-10 pb-6 text-white z-10">
          <button onClick={() => nav('/entregador/dashboard')} className="text-2xl mb-2 block">←</button>
          <h1 className="font-black text-lg">
            {step === 'accepted' ? 'Buscar o Gás' : 'A Caminho do Cliente'}
          </h1>
          <p className="text-orange-300 text-sm">Previsão: ~{order.estimatedMinutes} min</p>
        </div>
      </div>

      {/* Bottom panel */}
      <div className="bg-gray-900 px-4 py-4 rounded-t-3xl -mt-4 relative z-10">
        <div className="w-10 h-1 bg-gray-700 rounded mx-auto mb-4" />

        {/* Order info */}
        <div className="bg-gray-800 rounded-2xl p-3 flex items-center gap-3 mb-4">
          <span className="text-2xl">{order.brandEmoji}</span>
          <div className="flex-1">
            <div className="font-bold text-white text-sm">{order.brandName} — {order.sizeLabel}</div>
            <div className="text-xs text-gray-400">📍 {order.address}</div>
          </div>
          <div className="text-green-400 font-black">R$ {order.deliveryPrice?.toFixed(2)}</div>
        </div>

        {step === 'accepted' && (
          <button
            onClick={startTransit}
            className="w-full py-4 bg-primary rounded-2xl font-bold text-white text-lg active:scale-95 transition-all shadow-lg shadow-orange-500/30"
          >
            🛵 Iniciar Entrega
          </button>
        )}

        {step === 'in_transit' && (
          <button
            onClick={deliver}
            className="w-full py-4 bg-green-500 hover:bg-green-600 rounded-2xl font-bold text-white text-lg active:scale-95 transition-all shadow-lg shadow-green-500/30"
          >
            ✅ Confirmar Entrega
          </button>
        )}
      </div>
    </div>
  );
}
